import { useEffect, useRef } from "react";
import { useClearCart } from "../../cart/hooks/useClearCart";
import { trackPurchase } from "../helpers/trackPurchase";
import { TransformedOrder } from "../types/orderApiTypes";

const STORAGE_KEY_LATEST_ORDER = "latest_order_id";

/**
 * Handles post-purchase logic:
 * 1. Checks if the current order matches the one just placed in this session.
 * 2. If matched:
 *    - Clears the cart (Backend + Frontend).
 *    - Fires analytics 'purchase' events.
 *    - Cleans up session storage to prevent duplicate actions.
 */
export const useOrderProcessing = (
  order: TransformedOrder | undefined,
  isOrderLoaded: boolean,
  isHistoryView: boolean
) => {
  const { handleClearCart } = useClearCart();
  const hasProcessed = useRef(false);

  useEffect(() => {
    // Guard clauses
    if (!isOrderLoaded || !order || hasProcessed.current || isHistoryView) {
      return;
    }

    // 1. Get the ID stored during checkout submission
    const latestPlacedOrderId = sessionStorage.getItem(
      STORAGE_KEY_LATEST_ORDER
    );

    // 2. Logic: Process ONLY if current Order ID matches the ID saved during checkout
    if (latestPlacedOrderId === String(order.id)) {
      console.log(
        "[useOrderProcessing] Confirmed new order. Executing post-purchase actions..."
      );

      // A. Clear the Cart
      handleClearCart();

      // B. Track Purchase (GA4 / Meta)
      trackPurchase(order);

      // C. Clean up storage to prevent double-firing
      sessionStorage.removeItem(STORAGE_KEY_LATEST_ORDER);
    }

    // Mark as processed for this mount lifecycle
    hasProcessed.current = true;
  }, [isOrderLoaded, order, isHistoryView, handleClearCart]);
};
